import { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiContext } from '@api/api';
import { ExportJobStatusOutDTO, ExportJobStatusEnum, EntityTypeEnum, ExportJobRequestInDTO, ExportColumnInDTO, ExportFormatEnum, ExportDownloadUrlOutDTO } from '@api/ringil-api.v2';
import { triggerHiddenIframeDownload } from '@utils/downloadViaHiddenIframe';
import { useAuth0 } from '@auth0/auth0-react';
import useTranslationLgs from '@hooks/i18n/useTranslation';

// Local storage key for per-jobId export state
const EXPORT_STATE_KEY_PREFIX = 'ringil-export-state:'; // jobId will be appended

// Type for export state data (serializable to localStorage)
export interface ExportStateData {
  uiStatus: 'idle' | 'starting' | 'polling' | 'ready' | 'error' | 'cancelled';
  jobId: string | null;
  entityType: EntityTypeEnum | null;
  entityLabel: string | null;
  processedCount: number;
  totalCount: number | null;
  errorMessage: string | null;
  exportFormat?: ExportFormatEnum;
  userId?: string; // Add user ID to track which user started the export
  runAs?: string | null; // Impersonation context the export was started under; poll/show only when current URL runAs matches
}

// Track recent startExportJob calls to detect Strict Mode artifacts
interface RecentExportCall {
  entityType: EntityTypeEnum;
  timestamp: number;
}

// localStorage helpers
const loadExportState = (jobId: string): ExportStateData | null => {
  try {
    const data = localStorage.getItem(`${EXPORT_STATE_KEY_PREFIX}${jobId}`);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    console.error(`Failed to load export state for jobId ${jobId}`, e);
    return null;
  }
};

const saveExportState = (jobId: string, state: ExportStateData) => {
  try {
    localStorage.setItem(`${EXPORT_STATE_KEY_PREFIX}${jobId}`, JSON.stringify(state));
  } catch (e) {
    console.error(`Failed to save export state for jobId ${jobId}`, e);
  }
};

const removeExportState = (jobId: string) => {
  try {
    localStorage.removeItem(`${EXPORT_STATE_KEY_PREFIX}${jobId}`);
  } catch (e) {
    console.error(`Failed to remove export state for jobId ${jobId}`, e);
  }
};


/**
 * Custom hook to manage export state and actions.
 * Handles:
 * - Multiple concurrent exports
 * - Polling intervals per jobId
 * - localStorage persistence
 * - Smart 409 conflict detection (dev vs production)
 * - Download handling
 */
export function useExportState() {
  const api = useContext(ApiContext);
  const { tr } = useTranslationLgs();
  const { user } = useAuth0(); // Get current user from Auth0
  const currentUserId = user?.sub; // Auth0 user ID
  const [searchParams] = useSearchParams();
  const currentRunAs = searchParams.get('runAs'); // null when not impersonating
  
  const [activeExports, setActiveExports] = useState<Record<string, ExportStateData>>(() => {
    // Load ONLY actively polling exports from localStorage for the CURRENT USER
    // Skip stale exports, completed exports, and exports from other users
    // Note: On first render, currentUserId might be undefined (Auth0 loading),
    // so we return empty and let the effect below handle loading once user is available
    if (!currentUserId) {
      return {};
    }
    
    try {
      const stored: Record<string, ExportStateData> = {};
      
      // Iterate through all localStorage keys and find export states
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(EXPORT_STATE_KEY_PREFIX)) {
          const jobId = key.replace(EXPORT_STATE_KEY_PREFIX, '');
          const data = loadExportState(jobId);
          
          // Filter by: current user + polling/ready states
          // Keep polling state to show progress if user reloads during export
          if (data && data.userId === currentUserId && (data.uiStatus === 'polling' || data.uiStatus === 'ready')) {
            stored[jobId] = data;
          } else if (data) {
            // Remove exports from other users or stale exports
            localStorage.removeItem(`${EXPORT_STATE_KEY_PREFIX}${jobId}`);
          }
        }
      }
      
      console.log('[useExportState] Loaded for user', currentUserId, ':', Object.keys(stored));
      return stored;
    } catch (e) {
      console.error('[useExportState] Failed to load from localStorage', e);
      return {};
    }
  });

  
  // Track polling intervals per jobId
  const intervalsRef = useRef<Map<string, number>>(new Map());
  // Track which jobs have already downloaded
  const downloadedJobsRef = useRef<Set<string>>(new Set());
  // Track which jobIds are currently polling
  const pollingJobsRef = useRef<Set<string>>(new Set());
  // Track recent startExport calls for Strict Mode detection (100ms window)
  const recentExportCallsRef = useRef<RecentExportCall[]>([]);
  
  // Keep reference to latest API to avoid stale closures
  const apiRef = useRef(api);
  useEffect(() => {
    apiRef.current = api;
  }, [api]);

  // Flip an export to the 'error' state so the banner (which stays on the list the user started
  // from) surfaces the failure. Held in a ref so the polling effect can call it without listing
  // tr as a dep — that would tear down and rebuild the polling intervals.
  const markDownloadFailedRef = useRef<(jobId: string) => void>(() => {});
  markDownloadFailedRef.current = (jobId: string) => {
    const message = tr('export.downloadFailedDetail', 'the download link is unavailable, please run the export again');
    setActiveExports((prev) =>
      prev[jobId] ? { ...prev, [jobId]: { ...prev[jobId], uiStatus: 'error', errorMessage: message } } : prev,
    );
  };

  // Cleanup all intervals on unmount
  useEffect(() => {
    return () => {
      intervalsRef.current.forEach((intervalId) => {
        window.clearInterval(intervalId);
      });
      intervalsRef.current.clear();
    };
  }, []);

  // Load exports for current user once Auth0 finishes loading
  // KEY: Don't restore exports until we have a valid currentUserId
  // This prevents showing jobs from other users
  useEffect(() => {
    if (!currentUserId) {
      // Clear state while waiting for authentication
      // This ensures we never accidentally show other users' data
      setActiveExports({});
      return;
    }
    
    // NOW we have a valid user - load only THEIR exports in polling state
    const stored: Record<string, ExportStateData> = {};
    
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(EXPORT_STATE_KEY_PREFIX)) {
          const jobId = key.replace(EXPORT_STATE_KEY_PREFIX, '');
          const data = loadExportState(jobId);
          
          // Only restore if:
          // 1. In polling state (actively running)
          // 2. Belongs to current user
          if (data && data.userId === currentUserId && data.uiStatus === 'polling') {
            stored[jobId] = data;
          } else if (data) {
            // Remove exports from other users or stale exports
            localStorage.removeItem(`${EXPORT_STATE_KEY_PREFIX}${jobId}`);
          }
        }
      }
      
      console.log('[useExportState] Load effect: Loaded exports for authenticated user:', currentUserId, '→', Object.keys(stored));
      console.log('[useExportState] Load effect: Setting activeExports to:', stored);
      setActiveExports(stored);
    } catch (e) {
      console.error('[useExportState] Failed to load exports for user', e);
      setActiveExports({});
    }
  }, [currentUserId]);

  // Persist each export to localStorage when it changes
  useEffect(() => {
    console.log('[useExportState] Persist effect: activeExports changed to keys:', Object.keys(activeExports));
    Object.entries(activeExports).forEach(([jobId, state]) => {
      saveExportState(jobId, state);
    });
  }, [activeExports]);

  // Polling effect - poll all active exports
  useEffect(() => {
    // Track which jobs we're about to poll
    const jobsToPoll = new Set<string>();
    
    // Start polling for each job that's in polling state
    Object.entries(activeExports).forEach(([jobId, exportData]) => {
      // Skip if not in polling state
      if (exportData.uiStatus !== 'polling') {
        return;
      }

      // Skip if the current page is not the impersonation context the export was started under.
      // getJobStatus would 403 (job belongs to the impersonated user); pause until runAs matches again.
      if ((exportData.runAs ?? null) !== (currentRunAs ?? null)) {
        return;
      }

      jobsToPoll.add(jobId);

      // Skip if we already have an interval running for this job
      if (intervalsRef.current.has(jobId)) {
        return;
      }

      // Start polling for this specific job
      const intervalId = window.setInterval(() => {
        apiRef.current.getJobStatus(jobId, (d: ExportJobStatusOutDTO) => {
          // Update this specific export
          setActiveExports((prev) => ({
            ...prev,
            [jobId]: {
              ...prev[jobId],
              processedCount: d.processedCount,
              totalCount: d.totalCount ?? null,
            },
          }));

          // Artifact ready - trigger download (only once per job)
          if (d.artifactReady && !downloadedJobsRef.current.has(jobId)) {
            downloadedJobsRef.current.add(jobId);

            // Stop polling for this job IMMEDIATELY
            const interval = intervalsRef.current.get(jobId);
            if (interval) {
              window.clearInterval(interval);
              intervalsRef.current.delete(jobId);
            }

            // CRITICAL: Change state to 'ready' FIRST so polling effect stops
            // This prevents polling effect from creating new intervals
            setActiveExports((prev) => ({
              ...prev,
              [jobId]: {
                ...prev[jobId],
                uiStatus: 'ready',
              },
            }));

            // Then trigger download asynchronously
            // Download happens in background, banner shows "ready" state
            apiRef.current.downloadExport(
              jobId,
              (data: ExportDownloadUrlOutDTO) => {
                if (data?.url) {
                  triggerHiddenIframeDownload(data.url, () => markDownloadFailedRef.current(jobId));
                } else {
                  markDownloadFailedRef.current(jobId);
                }
              },
              null,
              {
                // The signed-URL request itself failed (404/403/expired): show it in the banner on
                // the list instead of the generic API error popup, so the user can retry.
                onError: () => markDownloadFailedRef.current(jobId),
              },
            );

            // Keep export in 'ready' state for 5 seconds so banner shows completion
            // Then auto-remove from state and localStorage
            window.setTimeout(() => {
              setActiveExports((prev) => {
                // A failed download flipped this to 'error'; keep it so the user can read/retry.
                if (prev[jobId]?.uiStatus === 'error') {
                  return prev;
                }
                removeExportState(jobId);
                const next = { ...prev };
                delete next[jobId];
                return next;
              });
            }, 5000); // 5 seconds
            
            return;
          }

          // Error state
          if (d.exportJobStatus === ExportJobStatusEnum.ERROR) {
            const interval = intervalsRef.current.get(jobId);
            if (interval) {
              window.clearInterval(interval);
              intervalsRef.current.delete(jobId);
            }
            // Remove from state and localStorage immediately - error exports don't need to display
            removeExportState(jobId);
            setActiveExports((prev) => {
              const next = { ...prev };
              delete next[jobId];
              return next;
            });
          }

          // Cancelled state
          if (d.exportJobStatus === ExportJobStatusEnum.CANCELLED) {
            const interval = intervalsRef.current.get(jobId);
            if (interval) {
              window.clearInterval(interval);
              intervalsRef.current.delete(jobId);
            }
            // Remove from state and localStorage immediately - cancelled exports don't need to display
            removeExportState(jobId);
            setActiveExports((prev) => {
              const next = { ...prev };
              delete next[jobId];
              return next;
            });
          }
        }, null, {
          // Background poll: don't surface the global API error toast (e.g. 403 from an in-flight
          // request fired just before navigating out of the impersonation context).
          onError: (error: any) => console.debug('[useExportState] getJobStatus poll error (suppressed)', jobId, error),
        });
      }, 4000) as unknown as number;

      // Store interval for this jobId
      intervalsRef.current.set(jobId, intervalId);
    });

    // Pause polling for jobs no longer eligible (e.g. left the impersonation context)
    intervalsRef.current.forEach((intervalId, jobId) => {
      if (!jobsToPoll.has(jobId)) {
        window.clearInterval(intervalId);
        intervalsRef.current.delete(jobId);
      }
    });

    // Update tracking of which jobs are polling
    pollingJobsRef.current = jobsToPoll;

    // Cleanup: clear intervals for jobs that are no longer polling
    return () => {
      // Get current polling jobs from ref (not stale activeExports)
      const currentPollingJobs = pollingJobsRef.current;
      
      // Clear any intervals for jobs not in the current polling set
      intervalsRef.current.forEach((intervalId, jobId) => {
        if (!currentPollingJobs.has(jobId)) {
          window.clearInterval(intervalId);
          intervalsRef.current.delete(jobId);
        }
      });
    };
  }, [activeExports, currentRunAs]);

  // Sync with backend when tab regains focus
  useEffect(() => {
    const handleFocus = () => {
      // Refresh all active exports from backend
      if (Object.keys(activeExports).length === 0) return;

      apiRef.current.listMyJobs((jobs: ExportJobStatusOutDTO[]) => {
        setActiveExports((prev) => {
          const next = { ...prev };
          Object.entries(next).forEach(([jobId, state]) => {
            const activeJob = jobs.find((j) => j.jobId === jobId);
            if (activeJob) {
              next[jobId] = {
                ...state,
                processedCount: activeJob.processedCount,
                totalCount: activeJob.totalCount ?? null,
                errorMessage: activeJob.errorMessage ?? null,
              };
            }
          });
          return next;
        });
      });
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [activeExports]);

  // Listen to storage changes from other tabs/components to sync state via localStorage
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (!event.key?.startsWith(EXPORT_STATE_KEY_PREFIX)) return;

      const jobId = event.key.replace(EXPORT_STATE_KEY_PREFIX, '');

      if (event.newValue === null) {
        // Export was deleted from localStorage
        setActiveExports((prev) => {
          const next = { ...prev };
          delete next[jobId];
          return next;
        });
      } else {
        // Export was updated in localStorage
        try {
          const updatedData = JSON.parse(event.newValue);

          // Only sync if export belongs to current user (user isolation)
          if (updatedData.userId === currentUserId) {
            setActiveExports((prev) => ({
              ...prev,
              [jobId]: updatedData,
            }));
          }
        } catch (e) {
          console.error('[useExportState] Failed to parse storage event data', e);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [currentUserId]);

  // Smart 409 conflict detection
  const isStrictModeArtifact = (entityType: EntityTypeEnum): boolean => {
    const now = Date.now();
    
    console.log('[isStrictModeArtifact] Called for entityType:', entityType);
    console.log('[isStrictModeArtifact] Recent calls before cleanup:', recentExportCallsRef.current);
    
    // Cleanup old calls (> 150ms window)
    recentExportCallsRef.current = recentExportCallsRef.current
      .filter(call => {
        const isOld = now - call.timestamp >= 150;
        if (isOld) {
          console.log('[isStrictModeArtifact] Removing old call:', call, 'age:', now - call.timestamp, 'ms');
        }
        return !isOld;
      });
    
    console.log('[isStrictModeArtifact] Recent calls after cleanup:', recentExportCallsRef.current);
    
    // Check if this conflict is from a recent call (within 100ms = Strict Mode artifact)
    const recentCall = recentExportCallsRef.current
      .find(call => {
        const isRecent = call.entityType === entityType && now - call.timestamp < 100;
        if (isRecent) {
          console.log('[isStrictModeArtifact] Found recent call for', entityType, ':', call, 'gap:', now - call.timestamp, 'ms');
        }
        return isRecent;
      });
    
    const result = !!recentCall;
    console.log('[isStrictModeArtifact] Result for', entityType, ':', result, '(', result ? 'StrictMode artifact' : 'Real conflict', ')');
    
    return result;
  };

  // Build actions
  const actions = useMemo(() => ({
    startExport: (entityType: EntityTypeEnum, entityLabel: string, filter: unknown, exportFormat: ExportFormatEnum, columns?: ExportColumnInDTO[] | null) => {
      // CRITICAL: Block export if user not authenticated yet
      // This prevents exports from being created with undefined userId
      if (!currentUserId) {
        console.warn('[useExportState] startExport: BLOCKED - waiting for user authentication (currentUserId not available)');
        return;
      }

      console.log('[useExportState] startExport: STARTING export for user', currentUserId, 'entityType:', entityType);

      const now = Date.now();
      const tempJobId = `${entityType}-${now}`;
      
      console.log('[useExportState] startExport: Created tempJobId:', tempJobId);
      
      // Track this call for Strict Mode detection
      console.log('[useExportState] startExport: 📍 Recording export call - entityType:', entityType, 'timestamp:', now);
      recentExportCallsRef.current.push({ entityType, timestamp: now });
      console.log('[useExportState] startExport: Recent calls now:', recentExportCallsRef.current);
      
      setActiveExports((prev) => {
        const newState: Record<string, ExportStateData> = {
          ...prev,
          [tempJobId]: {
            uiStatus: 'starting' as const,
            jobId: tempJobId,
            entityType,
            entityLabel,
            processedCount: 0,
            totalCount: null,
            errorMessage: null,
            exportFormat,
            userId: currentUserId, // ✅ NOW GUARANTEED TO BE DEFINED
            runAs: currentRunAs,
          },
        };
        console.log('[useExportState] startExport: Updated state with starting export:', Object.keys(newState), newState);
        return newState;
      });

      const body: ExportJobRequestInDTO = { entityType, filter: filter as any, exportFormat, ...(columns !== undefined ? { columns } : {}) } as any;
      console.log('[useExportState] startExport: Making API call with body:', body);
      
      apiRef.current.startExportJob(
        body,
        // onSuccess callback
        (data: any) => {
          console.log('[useExportState] startExport: API SUCCESS! Received jobId:', data.jobId);
          
          // Replace temp jobId with real one
          setActiveExports((prev) => {
            const next = { ...prev };
            console.log('[useExportState] startExport: Before delete, prev state keys:', Object.keys(prev));
            delete next[tempJobId];
            console.log('[useExportState] startExport: After delete tempJobId, state keys:', Object.keys(next));
            
            next[data.jobId] = {
              uiStatus: 'polling',
              jobId: data.jobId,
              entityType: data.entityType,
              entityLabel,
              totalCount: data.totalCount ?? null,
              processedCount: 0,
              errorMessage: null,
              exportFormat,
              userId: currentUserId, // Preserve user ID
              runAs: currentRunAs,
            };
            console.log('[useExportState] startExport: After adding real jobId, state keys:', Object.keys(next), 'Full state:', next);
            return next;
          });
        },
        null, // hashAuthentication
        // callbacks object
        {
          // Handle 409 Conflict - export already in progress for this entity
          onConflict: (conflictInfo: any) => {
            console.log('[onConflict] ⚠️ CONFLICT DETECTED for entityType:', entityType);
            console.log('[onConflict] conflictInfo:', conflictInfo);
            
            // Check if this is a Strict Mode artifact or real conflict
            const isArtifact = isStrictModeArtifact(entityType);
            
            if (isArtifact) {
              // Dev mode: Strict Mode double-invoke, silent
              console.debug(`[Export] ✅ Strict Mode 409 for ${entityType}, silently ignoring (this is React dev behavior)`);
              return;
            }

            // Production: Real conflict - show error to user
            console.warn(`[Export] ❌ REAL CONFLICT for ${entityType}:`, conflictInfo);
            
            // Remove temp entry and show conflict error
            setActiveExports((prev) => {
              const next = { ...prev };
              delete next[tempJobId];
              return next;
            });
            removeExportState(tempJobId);
            
            console.warn(`[Export] Removed temp entry ${tempJobId} due to real conflict`);
          },
          // Handle other errors
          onError: (error: any, showStandardError) => {
            // Remove temp entry on error
            setActiveExports((prev) => {
              const next = { ...prev };
              delete next[tempJobId];
              return next;
            });
            removeExportState(tempJobId);

            console.error(`[Export] Error on startExportJob:`, error);
            showStandardError();
          }
        }
      );
    },
    cancelExport: (jobId: string) => {
      apiRef.current.cancelExportJob(jobId, () => {
        const interval = intervalsRef.current.get(jobId);
        if (interval) {
          window.clearInterval(interval);
          intervalsRef.current.delete(jobId);
        }
        setActiveExports((prev) => ({
          ...prev,
          [jobId]: { ...prev[jobId], uiStatus: 'cancelled' },
        }));

        window.setTimeout(() => {
          removeExportState(jobId);
          setActiveExports((prev) => {
            const next = { ...prev };
            delete next[jobId];
            return next;
          });
        }, 3000);
      });
    },
    resetAll: () => {
      // Clear all exports from localStorage and state
      Object.keys(activeExports).forEach((jobId) => {
        removeExportState(jobId);
        const interval = intervalsRef.current.get(jobId);
        if (interval) {
          window.clearInterval(interval);
          intervalsRef.current.delete(jobId);
        }
      });
      setActiveExports({});
    },
  }), [activeExports, currentUserId, currentRunAs]);

  return {
    activeExports,
    ...actions,
  };
}

